import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { useEffectOnce } from 'react-use';
import { logError } from 'commons/logger';
import isChunkLoadError from '@/utils/isChunkLoadError';
import getEnvValue from '@/joystagram-components/commons/getEnvValue';
import scheduleIdleAfterLCP from '@/joystagram-components/commons/scheduleIdleAfterLCP';

// The RudderStack SDK must be instantiated exactly once per page load.
// RudderAnalyticsProvider wraps the whole app and can remount (hydration /
// Suspense churn observed on Dev and Staging). Because `new RudderAnalytics()`
// reassigns window.rudderanalytics to a fresh, not-yet-loaded instance, a
// remount would clobber the already-loaded instance — its anonymousId reads
// back as null and subsequent track() calls (e.g. add_to_cart) are silently
// dropped instead of reaching Google Tag Manager (CON-8265). Holding the
// instance in a module-level singleton makes initialisation idempotent across
// remounts so there is always exactly one live SDK instance.
let analyticsPromise = null;

// Load plugins + device-mode integrations from the first-party mirror, not the
// default cdn.rudderlabs.com which ad-blockers block — losing the GTM/GA4
// forwarders and under-reporting events (CON-8272). Public domain, not a secret.
const RUDDER_CDN_BASE = 'https://rudr-cdn1.joybird.com/v3/modern';
const loadRudderAnalytics = async () => {
  const {
    RudderAnalytics
  } = await import('@rudderstack/analytics-js');
  const analyticsInstance = new RudderAnalytics();
  analyticsInstance.load(getEnvValue('NEXT_PUBLIC_RUDDER_STACK_WRITE_KEY'), getEnvValue('NEXT_PUBLIC_RUDDER_STACK_DATA_PLANE_URL'), {
    pluginsSDKBaseURL: `${RUDDER_CDN_BASE}/plugins`,
    destSDKBaseURL: `${RUDDER_CDN_BASE}/js-integrations`
  });
  return analyticsInstance;
};

// JOYBIRD-40-5QA6 — a chunk failure here is a dropped connection or an ad-blocker,
// not a bug: analytics is optional telemetry. Real init failures still report.
const handleInitError = e => {
  if (isChunkLoadError(e)) return;
  logError(e, 'Error initializing RudderAnalytics instance');
};
const useRudderStackAnalytics = () => {
  const $ = _c(1);
  const [analytics, setAnalytics] = useState();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      if (analyticsPromise) {
        analyticsPromise.then(setAnalytics).catch(handleInitError);
        return;
      }
      const cancel = scheduleIdleAfterLCP(() => {
        analyticsPromise = loadRudderAnalytics();
        analyticsPromise.then(setAnalytics).catch(handleInitError);
      });
      return cancel;
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffectOnce(t0);
  return analytics;
};
export default useRudderStackAnalytics;